import { ref, computed, watch } from '@vue/composition-api'
import { debounce } from 'lodash'

/**
 * Composable para manejar la lógica de paginación de tablas
 * @param {Object} options - Opciones de configuración
 * @param {Function} options.fetchCallback - Función callback para obtener datos
 * @param {Number} options.initialPerPage - Cantidad inicial de items por página (default: 10)
 * @param {Array} options.perPageOptions - Opciones disponibles para items por página (default: [10, 25, 50, 100])
 * @param {String} options.initialSortBy - Campo inicial para ordenar (default: 'id')
 * @param {Boolean} options.initialSortDesc - Orden descendente inicial (default: true)
 * @param {Number} options.debounceTime - Tiempo de debounce para búsqueda (default: 500)
 * @returns {Object} - Objeto con propiedades y métodos de paginación
 */
export default function useTablePagination(options = {}) {
  const {
    fetchCallback = null,
    initialPerPage = 10,
    perPageOptions = [10, 25, 50, 100],
    initialSortBy = 'id',
    initialSortDesc = true,
    debounceTime = 500
  } = options

  // Referencias reactivas
  const refListTable = ref(null)
  const perPage = ref(initialPerPage)
  const totalList = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref(initialSortBy)
  const isSortDirDesc = ref(initialSortDesc)

  // Computed para metadata de paginación
  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  // Método para refrescar datos
  const refetchData = () => {
    if (refListTable.value && refListTable.value.refresh) {
      refListTable.value.refresh()
    } else if (fetchCallback) {
      fetchCallback()
    }
  }

  // Watch para cambios en paginación
  watch([currentPage, perPage], () => {
    refetchData()
  })

  // Watch para cambios en búsqueda (con debounce)
  watch([searchQuery], debounce(() => {
    // Resetear a primera página cuando se busca
    if (currentPage.value !== 1) {
      currentPage.value = 1
    } else {
      refetchData()
    }
  }, debounceTime))

  // Método para actualizar página desde componente de paginación
  const updatePagination = (page) => {
    currentPage.value = page
  }

  return {
    // Referencias
    refListTable,
    perPage,
    totalList,
    currentPage,
    searchQuery,
    sortBy,
    isSortDirDesc,

    // Opciones
    perPageOptions,

    // Computed
    dataMeta,

    // Métodos
    refetchData,
    updatePagination,
  }
}
